import BaseService from './BaseService';
import { getActorName } from 'shared/hooks/useActorName';
import { ActionItem, ActionStep, CampaignOption, CaseRecord, ChangeGroup, ChangeRow, CoverageOutcome, PerformanceRow, StoreSummary, UserCampaignGroup, WatchdogCheck, WatchdogCoverage, WatchdogHealth } from 'pages/GoogleWatchdog/types';

const VALID_COVERAGE_OUTCOMES = new Set<CoverageOutcome | 'not_checked'>(['clean', 'flagged', 'insufficient_data', 'not_applicable', 'skipped', 'not_checked']);

// Unlike the shared handleHttpRequest (which swallows errors and returns null), every
// method here rethrows the backend's message: the API's 409s carry human-readable
// conflict explanations ("item is not open, already claimed…") the UI must show.
function rethrow(err: any): never {
  throw new Error(err?.response?.data?.error ?? err?.message ?? 'request failed');
}

class GoogleWatchdogService extends BaseService {
  private writeConfig() {
    return { headers: { 'X-Sbly-Verified-User': getActorName() } };
  }

  async listItems(filters: { status?: string; store?: string; funnelStep?: string; owner?: string; q?: string; includeScheduled?: boolean; closed?: 'recovered' } = {}): Promise<ActionItem[]> {
    await this._getAccessToken();
    const params: Record<string, string | number> = {};
    // false/'' mean "no filter" and are omitted; numbers (incl. 0) must survive.
    for (const [k, v] of Object.entries(filters)) if (v !== undefined && v !== '' && v !== false) params[k] = v === true ? 1 : v;
    const res = await this.http.get('google-watchdog/items', { params }).catch(rethrow);
    return res.data;
  }

  async listRecovered(): Promise<ActionItem[]> {
    return this.listItems({ closed: 'recovered' });
  }

  /** Google Ads totals per ad for the campaign table. Dates are inclusive YYYY-MM-DD. */
  async performance(params: { stores: string[]; from: string; to: string }): Promise<PerformanceRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/performance', { params: { stores: params.stores.join(','), from: params.from, to: params.to } }).catch(rethrow);
    return res.data;
  }

  async getItem(itemId: string): Promise<ActionItem> {
    await this._getAccessToken();
    const res = await this.http.get(`google-watchdog/items/${encodeURIComponent(itemId)}`).catch(rethrow);
    return res.data;
  }

  // Case history is best-effort. An item without a natural key returns an empty record,
  // while an unavailable case record should not break the detail view.
  async getCase(itemId: string): Promise<CaseRecord | null> {
    await this._getAccessToken();
    try {
      const res = await this.http.get(`google-watchdog/items/${encodeURIComponent(itemId)}/case`);
      if (!res.data || typeof res.data !== 'object' || Array.isArray(res.data)) return null;
      return res.data as CaseRecord;
    } catch (err) {
      console.error(`getCase failed for ${itemId}`, err);
      return null;
    }
  }

  async claim(itemId: string, unclaim: boolean): Promise<void> {
    await this._getAccessToken();
    await this.http.post(`google-watchdog/items/${encodeURIComponent(itemId)}/claim`, { unclaim }, this.writeConfig()).catch(rethrow);
  }

  async resolve(itemId: string, note: string | null, kind: 'platform_change' | 'offline_change'): Promise<void> {
    await this._getAccessToken();
    await this.http.post(`google-watchdog/items/${encodeURIComponent(itemId)}/resolve`, { note, kind }, this.writeConfig()).catch(rethrow);
  }

  async dismiss(itemId: string, reason: string | null): Promise<void> {
    await this._getAccessToken();
    await this.http.post(`google-watchdog/items/${encodeURIComponent(itemId)}/dismiss`, { reason }, this.writeConfig()).catch(rethrow);
  }

  async snooze(itemId: string, until: string): Promise<void> {
    await this._getAccessToken();
    await this.http.post(`google-watchdog/items/${encodeURIComponent(itemId)}/snooze`, { until }, this.writeConfig()).catch(rethrow);
  }

  async reopen(itemId: string): Promise<void> {
    await this._getAccessToken();
    await this.http.post(`google-watchdog/items/${encodeURIComponent(itemId)}/reopen`, {}, this.writeConfig()).catch(rethrow);
  }

  async listSteps(store: string): Promise<ActionStep[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/action-steps', { params: { store } }).catch(rethrow);
    return res.data.steps;
  }

  // Approving a Google Ads step makes the change at once (server-side, within its limits).
  async approveStep(stepId: string): Promise<ActionStep> {
    await this._getAccessToken();
    const res = await this.http.post(`google-watchdog/action-steps/${encodeURIComponent(stepId)}/approve`, {}, this.writeConfig()).catch(rethrow);
    return res.data.step;
  }

  async rejectStep(stepId: string, reason: string | null): Promise<ActionStep> {
    await this._getAccessToken();
    const res = await this.http.post(`google-watchdog/action-steps/${encodeURIComponent(stepId)}/reject`, { reason }, this.writeConfig()).catch(rethrow);
    return res.data.step;
  }

  async summary(): Promise<StoreSummary[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/summary').catch(rethrow);
    return res.data;
  }

  // GET /changes defaults to burst-grouped rows (one per human action). Rows from an
  // api that predates grouping lack event_count — normalize to 1 so the UI never
  // renders "undefined changes".
  async listChangeGroups(filters: { source?: string; store?: string; actor?: string; campaign?: string; from?: string; to?: string; q?: string; limit?: number; offset?: number } = {}): Promise<ChangeGroup[]> {
    await this._getAccessToken();
    const params: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(filters)) if (v !== undefined && v !== '') params[k] = v as string | number;
    const res = await this.http.get('google-watchdog/changes', { params }).catch(rethrow);
    return (res.data as ChangeGroup[]).map(r => ({ ...r, event_count: Number(r.event_count) || 1 }));
  }

  /** Raw member events of one burst, oldest first — powers the expanded burst row. */
  async listBurstMembers(burstId: string): Promise<ChangeRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/changes', { params: { burst: burstId } }).catch(rethrow);
    return res.data;
  }

  // Change events on the item's campaign (30d) for the detail cross-link panel.
  // Best-effort: 404 (unknown item / older api) or any failure renders as "no
  // changes" rather than breaking the detail page.
  async itemChanges(itemId: string): Promise<ChangeRow[]> {
    await this._getAccessToken();
    try {
      const res = await this.http.get(`google-watchdog/items/${encodeURIComponent(itemId)}/changes`);
      return Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  }

  // Every check the latest run ran on this campaign, for the "What we checked" section.
  // Best-effort: 404 / older api / any failure returns null, which hides the section
  // entirely instead of breaking the detail page.
  //
  // Validation is all-or-nothing on purpose. A check whose name, layer, or outcome is
  // missing came from an API that predates the check registry, and every check in that
  // payload is equally old — so the honest render is no section at all. Dropping the
  // unreadable ones instead would print a short list under a heading that promises the
  // full one, which is exactly the wrong direction for a section whose whole job is to
  // say what was and was not looked at.
  async getCoverage(itemId: string): Promise<WatchdogCoverage | null> {
    await this._getAccessToken();
    try {
      const res = await this.http.get(`google-watchdog/items/${encodeURIComponent(itemId)}/coverage`);
      if (!res.data || typeof res.data !== 'object' || Array.isArray(res.data) || !Array.isArray(res.data.checks)) return null;

      const checks: WatchdogCheck[] = [];
      for (const c of res.data.checks) {
        const check = typeof c?.check === 'string' ? c.check.trim() : '';
        const watchesFor = typeof c?.watchesFor === 'string' ? c.watchesFor.trim() : '';
        if (!check || !watchesFor) return null;
        if (c?.layer !== 'detection' && c?.layer !== 'diagnosis') return null;
        if (!VALID_COVERAGE_OUTCOMES.has(c?.outcome)) return null;
        checks.push({
          check,
          layer: c.layer,
          watchesFor,
          guardrail: typeof c?.guardrail === 'string' ? c.guardrail : undefined,
          wontClaim: typeof c?.wontClaim === 'string' ? c.wontClaim : undefined,
          checklistSummary: typeof c?.checklistSummary === 'string' ? c.checklistSummary : undefined,
          deckSlide: typeof c?.deckSlide === 'string' ? c.deckSlide : undefined,
          evaluator: typeof c?.evaluator === 'string' ? c.evaluator : '',
          outcome: c.outcome,
          detail: typeof c?.detail === 'string' ? c.detail : null,
        });
      }
      if (!checks.length) return null;
      return { runDate: res.data.runDate ?? null, checks };
    } catch {
      return null;
    }
  }

  async logChange(body: { summary: string; store?: string; rationale?: string; oldValue?: string; newValue?: string; campaignId?: string; url?: string; unitType?: string; unitLabel?: string; changedAt?: string }): Promise<{ changeKey: string }> {
    await this._getAccessToken();
    const res = await this.http.post('google-watchdog/changes', body, this.writeConfig()).catch(rethrow);
    return res.data;
  }

  // Watchdog pipeline health for the queue banner. The latest run row, or null when the
  // endpoint is reachable but no run has been recorded yet; undefined means the fetch
  // itself failed (the banner treats that as red). Unlike the write methods above, this
  // never throws — health is best-effort and must not break the item list load.
  async getHealth(): Promise<WatchdogHealth | null | undefined> {
    await this._getAccessToken();
    try {
      const res = await this.http.get('google-watchdog/health');
      return res.data;
    } catch {
      return undefined;
    }
  }

  async listGroups(): Promise<UserCampaignGroup[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/groups').catch(rethrow);
    return res.data;
  }
  async listPickerCampaigns(): Promise<CampaignOption[]> {
    await this._getAccessToken();
    const res = await this.http.get('google-watchdog/groups/campaigns').catch(rethrow);
    return res.data.map((row: { campaign_id: string; campaign_name: string; store: string | null; status: string; live: boolean }) => ({
      campaignId: row.campaign_id, campaignName: row.campaign_name, store: row.store, status: row.status, live: row.live,
    }));
  }
  async saveGroup(body: { groupId?: string; groupName: string; campaignIds: string[] }): Promise<{ groupId: string }> {
    await this._getAccessToken();
    const res = await this.http.post('google-watchdog/groups', body, this.writeConfig()).catch(rethrow);
    return res.data; // { groupId }
  }
  async deleteGroup(groupId: string): Promise<void> {
    await this._getAccessToken();
    await this.http.delete(`google-watchdog/groups/${encodeURIComponent(groupId)}`, this.writeConfig()).catch(rethrow);
  }
}

export default GoogleWatchdogService;
